import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    CircularProgress,
    MenuItem,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import AtomicButton from '../../atomic/button/Button';
import AtomicCheckbox from '../../atomic/checkbox/Checkbox';
import InputBox from '../../atomic/inputbox/InputBox';
import GoogleAddressInput from '../../composed/googleaddressinput/GoogleAddressInput';
import { AddressDataContext } from '../../context-api/data-provider/GoogleAddressDataProvider';
import { isMobile } from '../../utils/ScreenSizeHelper';
import { isMobileNo, isValidEmail } from '../../utils/regexValidation';
import {
    onTemporaryPhoneVerificationAPI,
    onMerchantContactRequestAPI,
    onMarketingAvailableTimeSlotsAPI,
} from '../../context-api/api-services/Onloadinfo';
import PHONE_SMS from '../../utils/assets/png/phone_sms_icon_white_bg.png';

const HEARD_OPTIONS = [
    'A SingleCombo team member visited us',
    'Brochure / flyer',
    'QR code',
    'Referred by another restaurant',
    'Social media',
    'Online search',
    'Other',
];

const toApiDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value.trim())) {
        return value.trim();
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : '';
};

const toMonthKey = (value) => {
    if (!value) return '';
    const trimmed = String(value).trim();
    if (/^\d{2}\/\d{4}$/.test(trimmed)) return trimmed;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
        const [mm, , yyyy] = trimmed.split('/');
        return `${mm}/${yyyy}`;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('MM/YYYY') : '';
};

/** Earliest bookable day is 2 days from today (today + tomorrow blocked). */
const getEarliestBookableDate = () => dayjs().add(2, 'day').startOf('day');

const isDayClosedFromApi = (day) =>
    Boolean(day?.is_day_off || day?.day_status === 'unavailable');

const FIELDS = [
    { name: 'storeName', label: 'Restaurant / Store Name', maxLength: 80 },
    { name: 'contactPerson', label: 'Contact Person', maxLength: 60 },
    { name: 'designation', label: 'Designation', maxLength: 50 },
    { name: 'phone', label: 'Phone Number', maxLength: 10 },
    { name: 'email', label: 'Email', maxLength: 80 },
];

const EMPTY = {
    storeName: '',
    contactPerson: '',
    designation: '',
    phone: '',
    email: '',
    address: '',
    heard: '',
    preferredDate: '',
    preferredSlot: '',
};

const STATIC_CONTACT_DEFAULTS = {
    device_id: 'web_6f7e92a4d48e',
    team_to_contact: 'sales-new-partner',
    submitted_url: '/merchant-contact',
};

const WEB_DEVICE_ID_KEY = 'sc_merchant_contact_device_id';

function getWebDeviceId() {
    try {
        const existing = localStorage.getItem(WEB_DEVICE_ID_KEY);
        if (existing) return existing;
        const id = `web_${Math.random().toString(16).slice(2, 14)}`;
        localStorage.setItem(WEB_DEVICE_ID_KEY, id);
        return id;
    } catch {
        return STATIC_CONTACT_DEFAULTS.device_id;
    }
}

function parseStateAndZip(statePart = '') {
    const trimmed = String(statePart).trim();
    const match = trimmed.match(/^([A-Za-z. ]+?)\s+(\d{5})(?:-\d{4})?$/);
    if (match) {
        return { state: match[1].trim(), zipcode: match[2] };
    }
    return { state: trimmed, zipcode: '' };
}

function parseAddressParts(place) {
    const description = String(place?.formattedAddress || place?.description || '').trim();
    const parts = description
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);

    const addressLine = String(place?.addressLine || parts[0] || '').trim();
    const city = String(place?.city || parts[1] || '').trim();
    const { state, zipcode } = parseStateAndZip(place?.state || parts[2] || '');
    const country = String(place?.country || parts[3] || parts[parts.length - 1] || '').trim();

    return {
        formattedAddress: description,
        street: addressLine,
        city,
        state,
        zipcode: zipcode || String(place?.zipcode || '').trim(),
        country,
        county: String(place?.county || '').trim(),
        googlePlaceId: place?.place_id || place?.googlePlaceId || '',
        coordinates: Array.isArray(place?.coordinates) ? place.coordinates : null,
    };
}

function buildGeolocation(place, selectedAddress) {
    const parts = parseAddressParts({
        ...place,
        description: place?.description || place?.formattedAddress || selectedAddress,
    });

    return {
        type: 'Point',
        coordinates: parts.coordinates || [0, 0],
        formattedAddress: parts.formattedAddress || selectedAddress || '',
        street: parts.street || '',
        city: parts.city || '',
        state: parts.state || '',
        zipcode: parts.zipcode || '',
        country: parts.country || '',
        county: parts.county || '',
        googlePlaceId: parts.googlePlaceId || '',
    };
}

function buildMerchantContactPayload({ values, phone_no, selectedAddress, selectedPlace, meta }) {
    const payload = {
        restaurant_store_name: values.storeName.trim() || 'Restaurant',
        contact_person: values.contactPerson.trim() || 'Contact',
        designation: values.designation.trim() || 'Owner',
        phone_number: phone_no || '',
        email: String(values.email || '').trim(),
        restaurant_address: selectedAddress,
        how_did_you_hear_about_us: values.heard || 'Restaurant Visit',
        device_id: meta.device_id || getWebDeviceId() || STATIC_CONTACT_DEFAULTS.device_id,
        team_to_contact: meta.team_to_contact || STATIC_CONTACT_DEFAULTS.team_to_contact,
        submitted_url:
            meta.submitted_url ||
            (typeof window !== 'undefined' ? window.location.pathname : '') ||
            STATIC_CONTACT_DEFAULTS.submitted_url,
        Geolocation: buildGeolocation(selectedPlace, selectedAddress),
        appointment_date: toApiDate(values.preferredDate),
        appointment_time_slot: values.preferredSlot || '',
    };

    if (meta.contact_reason) {
        payload.contact_reason = meta.contact_reason;
    }
    if (meta.reasonLabel) {
        payload.reasonLabel = meta.reasonLabel;
    }

    return payload;
}

function fetchPlaceDetails(placeId) {
    return new Promise((resolve) => {
        if (!placeId || !window.google?.maps?.places?.PlacesService) {
            resolve(null);
            return;
        }

        try {
            const service = new window.google.maps.places.PlacesService(document.createElement('div'));
            service.getDetails(
                {
                    placeId,
                    fields: ['place_id', 'formatted_address', 'geometry', 'address_components'],
                },
                (result, status) => {
                    if (status !== window.google.maps.places.PlacesServiceStatus.OK || !result) {
                        resolve(null);
                        return;
                    }

                    const components = result.address_components || [];
                    const getComponent = (type) =>
                        components.find((c) => c.types?.includes(type))?.long_name || '';
                    const getShort = (type) =>
                        components.find((c) => c.types?.includes(type))?.short_name || '';

                    const lat = result.geometry?.location?.lat?.();
                    const lng = result.geometry?.location?.lng?.();

                    resolve({
                        place_id: result.place_id || placeId,
                        formattedAddress: result.formatted_address || '',
                        description: result.formatted_address || '',
                        addressLine:
                            [getComponent('street_number'), getComponent('route')].filter(Boolean).join(' ') ||
                            getComponent('premise'),
                        city:
                            getComponent('locality') ||
                            getComponent('sublocality') ||
                            getComponent('postal_town'),
                        state: getComponent('administrative_area_level_1') || getShort('administrative_area_level_1'),
                        zipcode: getComponent('postal_code'),
                        country: getComponent('country'),
                        county: getComponent('administrative_area_level_2'),
                        coordinates:
                            Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null,
                    });
                }
            );
        } catch (error) {
            console.log('fetchPlaceDetails error', error);
            resolve(null);
        }
    });
}

function digitsOnly(phone) {
    return String(phone || '').replace(/\D/g, '').slice(0, 10);
}

function formatOtpTime(seconds) {
    if (seconds === null || seconds < 0) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function getExptimeFromResponse(data) {
    return data?.exptime ?? data?.data?.exptime ?? null;
}

function getApiErrorMessage(response, fallback) {
    if (response?.hasError) {
        return (
            response?.message?.customMessage ||
            response?.message?.CustomMessage ||
            response?.message?.ErrorMessage ||
            (typeof response?.message === 'string' ? response.message : null) ||
            fallback
        );
    }
    const payload = response?.data || {};
    return (
        payload.customMessage ||
        payload.CustomMessage ||
        payload.errors?.[0]?.ErrorMessage ||
        fallback
    );
}

/**
 * @param {function} onSubmit  async (values) => {}  — your API call. Omit for demo mode.
 * @param {object}   meta      extra data merged into the payload (e.g. { repcode })
 * @param {function} onDone    called once the lead is submitted (e.g. to auto-close the modal)
 * @param {function} onStepChange  called when step changes: form | otp | done
 */
const MerchantContactForm = ({ onSubmit, meta = {}, onDone, onStepChange }) => {
    const { googleAddress } = useContext(AddressDataContext);
    const [values, setValues] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('form'); // form | otp | done
    const [otp, setOtp] = useState('');
    const [otpInfo, setOtpInfo] = useState('');
    const [remainingSeconds, setRemainingSeconds] = useState(null);
    const [canResend, setCanResend] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [smsConsent, setSmsConsent] = useState(false);
    const [monthDays, setMonthDays] = useState([]);
    const [loadedMonth, setLoadedMonth] = useState('');
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [slotsError, setSlotsError] = useState('');
    const monthSlotsRequestIdRef = useRef(0);

    const mobile = isMobile;
    // Viewport-based (not screen.availWidth) so DevTools / real phones stack correctly
    const isNarrowViewport = useMediaQuery('(max-width:900px)');
    const fieldWidth = '100%';
    const phone_no = digitsOnly(values.phone);
    const selectedAddress = (
        selectedPlace?.formattedAddress ||
        selectedPlace?.description ||
        values.address ||
        googleAddress?.description ||
        ''
    ).trim();

    useEffect(() => {
        if (onStepChange) onStepChange(step);
    }, [step, onStepChange]);

    const fetchMonthSlots = async (dateForMonth) => {
        const apiDate = toApiDate(dateForMonth) || dayjs().format('MM/DD/YYYY');
        const monthKey = toMonthKey(apiDate) || dayjs().format('MM/YYYY');
        const requestId = ++monthSlotsRequestIdRef.current;

        setSlotsLoading(true);
        setSlotsError('');
        // Drop previous month data so new-month days stay disabled until this fetch completes
        setMonthDays([]);
        setLoadedMonth('');

        try {
            const response = await onMarketingAvailableTimeSlotsAPI({ date: apiDate });
            if (requestId !== monthSlotsRequestIdRef.current) return;

            if (response?.hasError || !response?.data?.success) {
                setMonthDays([]);
                setLoadedMonth('');
                setSlotsError(
                    getApiErrorMessage(response, 'Unable to load available time slots. Please try again.')
                );
                return;
            }

            const days = Array.isArray(response?.data?.data?.days) ? response.data.data.days : [];
            setMonthDays(days);
            setLoadedMonth(response?.data?.data?.month || monthKey);
        } catch (err) {
            if (requestId !== monthSlotsRequestIdRef.current) return;
            console.error(err);
            setMonthDays([]);
            setLoadedMonth('');
            setSlotsError('Unable to load available time slots. Please try again.');
        } finally {
            if (requestId === monthSlotsRequestIdRef.current) {
                setSlotsLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchMonthSlots(dayjs().format('MM/DD/YYYY'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedDaySlots = (() => {
        if (!values.preferredDate) return [];
        const apiDate = toApiDate(values.preferredDate);
        const day = monthDays.find((item) => item.date === apiDate);
        return Array.isArray(day?.slots) ? day.slots : [];
    })();

    const availableDaySlots = selectedDaySlots.filter((slot) => slot.is_available);

    const closedDates = (() => {
        const set = new Set();
        monthDays.forEach((day) => {
            if (isDayClosedFromApi(day) && day?.date) set.add(day.date);
        });
        return set;
    })();

    const isClosedCalendarDay = (date) => {
        if (!date?.isValid()) return false;
        return closedDates.has(date.format('MM/DD/YYYY'));
    };

    const shouldDisableVisitDate = (date) => {
        if (!date?.isValid()) return true;
        if (date.isBefore(getEarliestBookableDate(), 'day')) return true;
        // Block all days until the open calendar month's availability is loaded
        if (slotsLoading || !loadedMonth || date.format('MM/YYYY') !== loadedMonth) {
            return true;
        }
        return isClosedCalendarDay(date);
    };

    const ClosedAwareDay = (dayProps) => {
        const { day, outsideCurrentMonth, ...other } = dayProps;
        const closed = !outsideCurrentMonth && isClosedCalendarDay(day);
        const disabled = Boolean(other.disabled) || closed;
        return (
            <PickersDay
                {...other}
                day={day}
                outsideCurrentMonth={outsideCurrentMonth}
                disabled={disabled}
                sx={
                    disabled
                        ? {
                              color: '#9ca3af !important',
                              opacity: 0.55,
                              '&.Mui-disabled': {
                                  color: '#9ca3af !important',
                                  opacity: 0.55,
                              },
                          }
                        : undefined
                }
            />
        );
    };

    useEffect(() => {
        const nextAddress = (googleAddress?.description || '').trim();
        // GoogleAddressInput clears context on unmount (OTP step) — keep the user's choice
        if (!nextAddress || !googleAddress?.place_id) return;

        setValues((prev) =>
            prev.address === nextAddress ? prev : { ...prev, address: nextAddress }
        );
        setErrors((prev) => (prev.address ? { ...prev, address: '' } : prev));

        let cancelled = false;
        setSelectedPlace((prev) => ({
            ...(prev || {}),
            ...googleAddress,
            description: nextAddress,
            formattedAddress: nextAddress,
        }));

        fetchPlaceDetails(googleAddress.place_id).then((details) => {
            if (cancelled || !details) return;
            setSelectedPlace((prev) => ({
                ...(prev || {}),
                ...googleAddress,
                ...details,
                description: details.formattedAddress || nextAddress,
            }));
            setValues((prev) => ({
                ...prev,
                address: details.formattedAddress || nextAddress,
            }));
        });

        return () => {
            cancelled = true;
        };
    }, [googleAddress]);

    useEffect(() => {
        if (remainingSeconds === null || remainingSeconds <= 0) {
            return undefined;
        }

        const timerId = setInterval(() => {
            setRemainingSeconds((prev) => {
                if (prev === null || prev <= 1) {
                    clearInterval(timerId);
                    setCanResend(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timerId);
    }, [remainingSeconds]);

    const startOtpCooldown = (data) => {
        const exptime = getExptimeFromResponse(data);
        if (!exptime) {
            setRemainingSeconds(0);
            setCanResend(true);
            return;
        }
        const seconds = Math.max(0, Math.floor((exptime - Date.now()) / 1000));
        setRemainingSeconds(seconds);
        setCanResend(seconds <= 0);
    };

    const handleChange = (name) => (e) => {
        const raw = e?.target?.value ?? '';
        if (name === 'phone') {
            if (String(raw).length <= 10) {
                setValues((prev) => ({ ...prev, phone: raw }));
            }
        } else {
            setValues((prev) => ({ ...prev, [name]: raw }));
        }
        setErrors((prev) => (prev[name] || prev.form ? { ...prev, [name]: '', form: '' } : prev));
    };

    const validate = () => {
        const next = {};
        FIELDS.forEach((f) => {
            if (!values[f.name].trim()) next[f.name] = 'Required';
        });
        if (!next.phone && !isMobileNo(phone_no)) {
            next.phone = 'Invalid mobile phone number format';
        }
        if (!next.email && !isValidEmail(values.email.trim())) {
            next.email = 'Invalid email format';
        }
        if (!selectedAddress) next.address = 'Required';
        if (!values.heard) next.heard = 'Required';
        if (!values.preferredDate) {
            next.preferredDate = 'Please select a visit date';
        } else if (shouldDisableVisitDate(dayjs(values.preferredDate))) {
            next.preferredDate = 'Selected date is not available';
        }
        if (!values.preferredSlot) {
            next.preferredSlot = 'Please select a time slot';
        } else if (slotsLoading) {
            next.preferredSlot = 'Please wait for available time slots to load';
        } else if (
            !availableDaySlots.some((slot) => slot.time_slot === values.preferredSlot)
        ) {
            next.preferredSlot = 'Selected time slot is unavailable';
        }
        if (!smsConsent) {
            next.smsConsent = 'Please agree to receive the SMS verification code';
        }
        return next;
    };

    const generateOtp = async () => {
        const response = await onTemporaryPhoneVerificationAPI({
            serviceType: 'generateOtp',
            phone_no,
        });

        if (response?.hasError || !response?.data?.success) {
            return {
                ok: false,
                message: getApiErrorMessage(response, 'Unable to send OTP. Please try again.'),
            };
        }

        return { ok: true, data: response.data };
    };

    const handleRequestCall = async () => {
        const found = validate();
        setErrors(found);
        if (Object.keys(found).length) return;

        setLoading(true);
        setErrors({});
        setOtpInfo('');
        try {
            const result = await generateOtp();
            if (!result.ok) {
                setErrors({ form: result.message, phone: result.message });
                return;
            }
            setOtp('');
            setStep('otp');
            startOtpCooldown(result.data);
            setOtpInfo(result.data?.customMessage || result.data?.CustomMessage || 'OTP sent');
        } catch (err) {
            console.error(err);
            setErrors({ form: 'Something went wrong. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp || otp.length !== 5) {
            setErrors({ form: 'Please enter the 5-digit verification code.' });
            return;
        }

        setLoading(true);
        setErrors({});
        setOtpInfo('');
        try {
            const response = await onTemporaryPhoneVerificationAPI({
                serviceType: 'verifyOtpNo',
                phone_no,
                otpNo: otp,
            });

            if (response?.hasError || !response?.data?.success) {
                setErrors({
                    form: getApiErrorMessage(response, 'Invalid OTP. Please try again.'),
                });
                return;
            }

            const contactPayload = buildMerchantContactPayload({
                values,
                phone_no,
                selectedAddress,
                selectedPlace,
                meta,
            });

            const contactResponse = await onMerchantContactRequestAPI(contactPayload);
            if (contactResponse?.hasError || contactResponse?.data?.success === false) {
                setErrors({
                    form: getApiErrorMessage(
                        contactResponse,
                        'Phone verified, but we could not submit your details. Please try again.'
                    ),
                });
                return;
            }

            const payload = {
                ...values,
                ...meta,
                phone_no,
                address: selectedAddress,
                phoneVerified: true,
                contactRequest: contactPayload,
            };
            if (onSubmit) await onSubmit(payload);
            setStep('done');
            if (onDone) onDone(payload);
        } catch (err) {
            console.error(err);
            setErrors({ form: 'Something went wrong. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (!canResend || loading) return;

        setLoading(true);
        setErrors({});
        setOtpInfo('');
        setCanResend(false);
        try {
            const result = await generateOtp();
            if (!result.ok) {
                setErrors({ form: result.message });
                setCanResend(true);
                return;
            }
            setOtp('');
            startOtpCooldown(result.data);
            setOtpInfo(result.data?.customMessage || result.data?.CustomMessage || 'OTP resent');
        } catch (err) {
            console.error(err);
            setErrors({ form: 'Something went wrong. Please try again.' });
            setCanResend(true);
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- Thank-you state ---------------- */
    if (step === 'done') {
        const firstName = values.contactPerson.trim().split(' ')[0] || 'there';
        return (
            <Box sx={{ textAlign: 'center', px: mobile ? 0.5 : 2, py: mobile ? 2 : 3 }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 52, color: '#45A517', mb: 1.5 }} />
                <Typography
                    sx={{
                        fontSize: mobile ? 18 : 20,
                        fontWeight: 700,
                        color: '#181436',
                        mb: 1,
                        fontFamily: 'Inter Medium, Segoe UI, Arial, sans-serif',
                    }}
                >
                    Thank you, {firstName}!
                </Typography>
                <Typography
                    sx={{
                        fontSize: mobile ? 14 : 14.5,
                        lineHeight: 1.6,
                        color: '#6b7280',
                        maxWidth: 400,
                        mx: 'auto',
                    }}
                >
                    We&apos;ve received your details for{' '}
                    <b style={{ color: '#181436' }}>{values.storeName}</b>. Our team will reach out soon — and
                    we&apos;ll give you a call before we visit, so you can pick a time that works for you.
                </Typography>
                <Box
                    sx={{
                        mt: 3,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1.25,
                        borderRadius: '8px',
                        border: '1px solid #FFD9B8',
                        bgcolor: '#FFF4ED',
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: '#181436',
                    }}
                >
                    <PhoneInTalkRoundedIcon sx={{ fontSize: 18, color: '#FF7A28' }} />
                    Expect a call at {phone_no}
                </Box>
            </Box>
        );
    }

    /* ---------------- OTP verify step ---------------- */
    if (step === 'otp') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box
                    component='button'
                    type='button'
                    onClick={() => {
                        if (loading) return;
                        setStep('form');
                        setOtp('');
                        setErrors({});
                        setOtpInfo('');
                        setRemainingSeconds(null);
                        setCanResend(false);
                    }}
                    sx={{
                        border: 'none',
                        background: 'transparent',
                        cursor: loading ? 'default' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: '#6b7280',
                        fontSize: 13,
                        p: 0,
                        mb: 2,
                        alignSelf: 'flex-start',
                        fontFamily: 'Inter Medium, Segoe UI, Arial, sans-serif',
                        '&:hover': { color: '#181436' },
                    }}
                >
                    <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                    Back
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isNarrowViewport ? 'column' : 'row',
                        alignItems: isNarrowViewport ? 'stretch' : 'center',
                        width: '100%',
                        gap: isNarrowViewport ? 2 : 0,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: isNarrowViewport ? 0 : '10px',
                            mb: isNarrowViewport ? 0.5 : 0,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            component='img'
                            src={PHONE_SMS}
                            alt='Phone SMS verification'
                            loading='eager'
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `${PHONE_SMS}?v=${Date.now()}`;
                            }}
                            sx={{
                                width: isNarrowViewport ? 120 : 180,
                                height: 'auto',
                                maxWidth: '100%',
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%', minWidth: 0 }}>
                        <Box
                            sx={{
                                mb: '16px',
                                color: '#3F3F6E',
                                fontSize: isNarrowViewport ? 22 : 28,
                                fontWeight: 700,
                                fontFamily: 'Inter Medium, Segoe UI, Arial, sans-serif',
                                textAlign: isNarrowViewport ? 'center' : 'left',
                            }}
                        >
                            Phone verification
                        </Box>

                        <Box
                            sx={{
                                mb: '8px',
                                fontSize: isNarrowViewport ? 13 : 14,
                                color: '#3F3F6E',
                                fontFamily: 'Inter Regular, Segoe UI, Arial, sans-serif',
                                textAlign: isNarrowViewport ? 'center' : 'left',
                            }}
                        >
                            We have sent a verification code to:{' '}
                            <Box
                                component='span'
                                sx={{ fontFamily: 'Inter SemiBold, Segoe UI, Arial, sans-serif' }}
                            >
                                {phone_no}
                            </Box>
                        </Box>

                        {errors.form && (
                            <Alert
                                severity='error'
                                sx={{
                                    width: '100%',
                                    maxWidth: 400,
                                    boxSizing: 'border-box',
                                    alignSelf: isNarrowViewport ? 'center' : 'stretch',
                                    border: 'solid 1px #E74C3C',
                                    mb: 1.25,
                                    py: isNarrowViewport ? 0.75 : 1,
                                    px: isNarrowViewport ? 1.25 : 2,
                                    backgroundColor: '#FDEDEB',
                                    color: 'red',
                                    '& .MuiAlert-icon': {
                                        fontSize: isNarrowViewport ? 20 : 22,
                                        mr: isNarrowViewport ? 1 : 1.5,
                                        py: 0,
                                        alignItems: 'center',
                                    },
                                    '& .MuiAlert-message': {
                                        minWidth: 0,
                                        overflowWrap: 'anywhere',
                                        padding: 0,
                                    },
                                }}
                            >
                                <AlertTitle
                                    sx={{
                                        fontSize: isNarrowViewport ? 13 : 14,
                                        fontWeight: 600,
                                        mb: 0.25,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    Error
                                </AlertTitle>
                                <Box sx={{ fontSize: isNarrowViewport ? 11 : 12, lineHeight: 1.4 }}>
                                    <strong>{errors.form}</strong>
                                </Box>
                            </Alert>
                        )}

                        {otpInfo && !errors.form && (
                            <Alert
                                severity='success'
                                sx={{
                                    width: '100%',
                                    maxWidth: 400,
                                    boxSizing: 'border-box',
                                    alignSelf: isNarrowViewport ? 'center' : 'stretch',
                                    border: 'solid 1px #04cc3dff',
                                    mb: 1.25,
                                    py: isNarrowViewport ? 0.75 : 1,
                                    px: isNarrowViewport ? 1.25 : 2,
                                    backgroundColor: '#E8F5E9',
                                    color: '#2E7D32',
                                    '& .MuiAlert-icon': {
                                        fontSize: isNarrowViewport ? 20 : 22,
                                        mr: isNarrowViewport ? 1 : 1.5,
                                        py: 0,
                                        alignItems: 'center',
                                    },
                                    '& .MuiAlert-message': {
                                        minWidth: 0,
                                        overflowWrap: 'anywhere',
                                        padding: 0,
                                    },
                                }}
                            >
                                <AlertTitle
                                    sx={{
                                        fontSize: isNarrowViewport ? 13 : 14,
                                        fontWeight: 600,
                                        mb: 0.25,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    Success
                                </AlertTitle>
                                <Box sx={{ fontSize: isNarrowViewport ? 11 : 12, lineHeight: 1.4 }}>{otpInfo}</Box>
                            </Alert>
                        )}

                        <Box
                            sx={{
                                mb: '10px',
                                fontSize: isNarrowViewport ? 13 : 14,
                                color: '#3F3F6E',
                                fontFamily: 'Inter Regular, Segoe UI, Arial, sans-serif',
                                textAlign: isNarrowViewport ? 'center' : 'left',
                            }}
                        >
                            Enter the 5 digit verification code you received to confirm your phone number.
                        </Box>

                        <InputBox
                            type='number'
                            label='Verification code'
                            size='small'
                            name='otp'
                            value={otp}
                            maxLength='5'
                            onChange={(event) => {
                                const value = String(event?.target?.value ?? '');
                                if (value.length <= 5) setOtp(value);
                                if (errors.form) setErrors({});
                            }}
                            sx={{
                                width: '100%',
                                maxWidth: isNarrowViewport ? '100%' : 400,
                                height: 'auto',
                                margin: '10px 0 20px 0',
                            }}
                        />

                        <AtomicButton
                            label={loading ? 'Verifying…' : 'Verify OTP'}
                            handleSubmit={handleVerifyOtp}
                            style={{
                                backgroundColor: '#181436',
                                width: '100%',
                                maxWidth: 400,
                            }}
                            loading={loading}
                            disabled={loading || otp.length !== 5}
                        />

                        <Box
                            sx={{
                                mt: '12px',
                                fontSize: 13,
                                color: '#3F3F6E',
                                fontFamily: 'Inter Regular, Segoe UI, Arial, sans-serif',
                                textAlign: isNarrowViewport ? 'center' : 'left',
                            }}
                        >
                            {remainingSeconds !== null && remainingSeconds > 0 ? (
                                <span>
                                    Resend OTP in{' '}
                                    <Box
                                        component='span'
                                        sx={{ fontFamily: 'Inter Bold, Segoe UI, Arial, sans-serif', fontWeight: 700 }}
                                    >
                                        {formatOtpTime(remainingSeconds)}
                                    </Box>
                                </span>
                            ) : (
                                <span>You can resend the OTP now.</span>
                            )}
                            <Box
                                component='span'
                                onClick={handleResendOtp}
                                sx={{
                                    ml: '8px',
                                    color: canResend && !loading ? '#5078e6' : '#A0A0A0',
                                    cursor: canResend && !loading ? 'pointer' : 'default',
                                    fontFamily: 'Inter SemiBold, Segoe UI, Arial, sans-serif',
                                }}
                            >
                                Resend OTP
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    /* ---------------- Form ---------------- */
    const renderField = (f, sx = {}) => (
        <InputBox
            key={f.name}
            type={f.name === 'phone' ? 'number' : f.name === 'email' ? 'email' : 'text'}
            label={f.label}
            size='small'
            name={f.name}
            value={values[f.name]}
            maxLength={String(f.maxLength)}
            error={Boolean(errors[f.name])}
            helperText={errors[f.name] || ''}
            onChange={handleChange(f.name)}
            sx={{
                width: fieldWidth,
                maxWidth: '100%',
                height: 'auto',
                margin: '8px 0px',
                ...sx,
            }}
        />
    );

    const storeNameField = FIELDS.find((f) => f.name === 'storeName');
    const contactPersonField = FIELDS.find((f) => f.name === 'contactPerson');
    const designationField = FIELDS.find((f) => f.name === 'designation');
    const phoneField = FIELDS.find((f) => f.name === 'phone');
    const emailField = FIELDS.find((f) => f.name === 'email');

    const rowFieldSx = {
        width: '100%',
        flex: 1,
        minWidth: 0,
        margin: '8px 0px',
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <GoogleAddressInput
                label='Restaurant Address'
                error={Boolean(errors.address)}
                helperText={errors.address || ''}
                styleAutoComplete={{
                    width: '100%',
                    maxWidth: '100%',
                    margin: '8px 0px',
                }}
            />

            {renderField(storeNameField)}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1.5,
                    width: '100%',
                    alignItems: 'flex-start',
                }}
            >
                {renderField(contactPersonField, rowFieldSx)}
                {renderField(designationField, rowFieldSx)}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1.5,
                    width: '100%',
                    alignItems: 'flex-start',
                }}
            >
                {renderField(phoneField, rowFieldSx)}
                {renderField(emailField, rowFieldSx)}
            </Box>

            <TextField
                select
                size='small'
                label='How did you hear about us?'
                name='heard'
                value={values.heard}
                error={Boolean(errors.heard)}
                helperText={errors.heard || ''}
                onChange={handleChange('heard')}
                className='custom-css-outlined-input'
                sx={{
                    width: fieldWidth,
                    maxWidth: '100%',
                    margin: '8px 0px',
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                    },
                }}
            >
                {HEARD_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt} sx={{ whiteSpace: 'normal' }}>
                        {opt}
                    </MenuItem>
                ))}
            </TextField>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isNarrowViewport ? 'column' : 'row',
                    gap: 1.5,
                    width: '100%',
                    alignItems: 'flex-start',
                    mt: 0.5,
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label='Preferred visit date'
                        value={values.preferredDate ? dayjs(values.preferredDate) : null}
                        minDate={getEarliestBookableDate()}
                        shouldDisableDate={shouldDisableVisitDate}
                        loading={slotsLoading}
                        renderLoading={() => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: 230,
                                    width: '100%',
                                }}
                            >
                                <CircularProgress size={28} sx={{ color: '#181436' }} />
                            </Box>
                        )}
                        slots={{ day: ClosedAwareDay }}
                        onMonthChange={(month) => {
                            if (!month?.isValid()) return;
                            const monthKey = month.format('MM/YYYY');
                            if (monthKey !== loadedMonth || slotsLoading) {
                                fetchMonthSlots(month.format('MM/DD/YYYY'));
                            }
                        }}
                        onChange={(value) => {
                            if (value?.isValid() && shouldDisableVisitDate(value)) {
                                return;
                            }
                            const nextDate = value?.isValid() ? value.format('YYYY-MM-DD') : '';
                            setValues((prev) => ({
                                ...prev,
                                preferredDate: nextDate,
                                preferredSlot: '',
                            }));
                            setErrors((prev) =>
                                prev.preferredDate || prev.preferredSlot || prev.form
                                    ? { ...prev, preferredDate: '', preferredSlot: '', form: '' }
                                    : prev
                            );
                            if (value?.isValid()) {
                                const monthKey = value.format('MM/YYYY');
                                if (monthKey !== loadedMonth) {
                                    fetchMonthSlots(value.format('MM/DD/YYYY'));
                                }
                            }
                        }}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                required: true,
                                error: Boolean(errors.preferredDate),
                                helperText:
                                    errors.preferredDate ||
                                    (slotsLoading ? 'Loading available dates…' : ''),
                                className: 'custom-css-outlined-input',
                                sx: {
                                    width: '100%',
                                    flex: isNarrowViewport ? 'none' : 1,
                                    minWidth: 0,
                                    margin: '8px 0px',
                                    backgroundColor: '#fff',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#fff',
                                    },
                                },
                            },
                        }}
                    />
                </LocalizationProvider>

                <TextField
                    select
                    required
                    size='small'
                    label='Preferred time slot'
                    name='preferredSlot'
                    value={values.preferredSlot}
                    disabled={!values.preferredDate || slotsLoading}
                    error={Boolean(errors.preferredSlot || slotsError)}
                    helperText={
                        errors.preferredSlot ||
                        slotsError ||
                        (!values.preferredDate
                            ? 'Select a date first'
                            : slotsLoading
                              ? 'Loading available times…'
                              : availableDaySlots.length === 0
                                ? 'No available slots for this date'
                                : '')
                    }
                    onChange={handleChange('preferredSlot')}
                    className='custom-css-outlined-input'
                    sx={{
                        width: '100%',
                        flex: isNarrowViewport ? 'none' : 1,
                        minWidth: 0,
                        margin: '8px 0px',
                        backgroundColor: '#fff',
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                        },
                    }}
                >
                    {selectedDaySlots.map((slot) => {
                        const remainingLabel =
                            slot.is_available && slot.remaining < slot.max_capacity
                                ? ` (${slot.remaining} left)`
                                : '';
                        const unavailableLabel = !slot.is_available ? ' (Unavailable)' : '';
                        return (
                            <MenuItem
                                key={slot.time_slot}
                                value={slot.time_slot}
                                disabled={!slot.is_available}
                            >
                                {slot.time_slot}
                                {remainingLabel}
                                {unavailableLabel}
                            </MenuItem>
                        );
                    })}
                </TextField>
            </Box>

            {errors.form && (
                <Typography sx={{ fontSize: 13, color: '#EE6969', mt: 1 }}>{errors.form}</Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', width: '100%', mt: 1.5 }}>
                <AtomicCheckbox
                    disabled={false}
                    checked={smsConsent}
                    onChange={() => {
                        setSmsConsent((v) => !v);
                        setErrors((prev) =>
                            prev.smsConsent || prev.form
                                ? { ...prev, smsConsent: '', form: '' }
                                : prev
                        );
                    }}
                    sx={{
                        padding: 0,
                        margin: '2px 8px 0 0',
                        color: errors.smsConsent ? '#EE6969' : '#3F3F6E',
                    }}
                />
                <Box
                    sx={{
                        fontSize: 12,
                        fontFamily: 'Inter Regular, Segoe UI, Arial, sans-serif',
                        color: '#3F3F6E',
                        textAlign: 'justify',
                        lineHeight: 1.45,
                    }}
                >
                    By checking this box, you agree to receive a one-time verification code via SMS
                    to confirm your phone number for account authentication. Consent is not a
                    condition of purchase. Message and data rates may apply. Reply STOP to opt out
                    and HELP for help.
                </Box>
            </Box>
            {errors.smsConsent && (
                <Typography sx={{ fontSize: 13, color: '#EE6969', mt: 0.5, width: '100%' }}>
                    {errors.smsConsent}
                </Typography>
            )}

            <AtomicButton
                label={loading ? 'Sending…' : 'Request a Call'}
                handleSubmit={handleRequestCall}
                style={{
                    backgroundColor: '#181436',
                    width: '100%',
                    marginTop: '18px',
                    marginBottom: '8px',
                }}
                loading={loading}
                disabled={loading || !smsConsent}
            />

            <Typography
                sx={{
                    fontSize: 12,
                    color: '#9ca3af',
                    textAlign: 'center',
                    mt: 0.5,
                    maxWidth: 400,
                    lineHeight: 1.5,
                }}
            >
                We only use this to contact you about partnering with us.
            </Typography>
        </Box>
    );
};

export default MerchantContactForm;
